import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  AuthenticationType,
  AuthOrganizationRole,
  AuthProjectRole,
  AuthFeaturePermission,
  AuthUser,
  AuthToken,
} from '../types/types.authentication';
import {
  AuthResponse,
  fetchSession,
  SessionResponse,
} from '@/services/auth-client';
import { isJwtExpired } from '@/utils/jwt';

const toAuthUser = (u: AuthResponse['user']): AuthUser => ({
  id: u.id,
  name: u.name ?? '',
  email: u.email,
});

const toOrganizationRole = (
  o: SessionResponse['organization_role'],
): AuthOrganizationRole =>
  o
    ? {
        id: o.id,
        organizationid: o.organization_id,
        role: o.role,
        organizationname: o.organization_name,
      }
    : ({} as AuthOrganizationRole);

const toProjectRole = (
  p: SessionResponse['project_roles'][number],
): AuthProjectRole => ({
  id: p.id,
  projectid: p.project_id,
  role: p.role,
  projectname: p.project_name,
});

const toFeaturePermission = (
  p: SessionResponse['feature_permissions'][number],
): AuthFeaturePermission => ({
  id: p.id,
  feature: p.feature,
  isenable: p.is_enable,
});

export const useAuthenticationStore = create<AuthenticationType>()(
  persist(
    (set, get) => ({
      currentUser: {} as AuthUser,
      setCurrentUser: (u: AuthUser) => set({ currentUser: u }),
      token: {} as AuthToken,
      setToken: (t: AuthToken) => set({ token: t }),
      refreshToken: '',
      updateAccessToken: (token: string, refreshToken: string) =>
        set({
          token: { token, tokentype: 'bearer', isexpired: false },
          refreshToken,
        }),
      setAuthentication: async (
        a: AuthResponse | undefined,
        callback: () => void,
      ) => {
        if (!a) {
          set({});
          callback();
          return;
        }

        set({
          currentUser: toAuthUser(a.user),
          token: {
            token: a.access_token,
            tokentype: 'bearer',
            isexpired: false,
          },
          refreshToken: a.refresh_token,
        });

        try {
          const session = await fetchSession(a.access_token);
          const projectRoles = session.project_roles.map(toProjectRole);
          const currentProjectRole = get().currentProjectRole;

          let newCurrentProjectRole = currentProjectRole;
          if (
            Object.keys(currentProjectRole).length === 0 &&
            projectRoles.length > 0
          ) {
            newCurrentProjectRole = projectRoles[0];
          } else if (projectRoles.length > 0) {
            const matchedRole = projectRoles.find(
              role => role.id === currentProjectRole.id,
            );
            newCurrentProjectRole = matchedRole || projectRoles[0];
          }

          set({
            currentUser: {
              ...toAuthUser(a.user),
              id: session.user.legacy_user_id ?? a.user.id,
            },
            organizationRole: toOrganizationRole(session.organization_role),
            projectRoles,
            currentProjectRole: newCurrentProjectRole,
            featurePermissions:
              session.feature_permissions.map(toFeaturePermission),
          });
        } catch (e) {
          // Login/registration itself succeeded; role resolution failing
          // shouldn't block the caller from proceeding (e.g. straight to
          // onboarding for a user with no organization yet).
        }

        callback();
      },
      // Checks real JWT expiry, not just token presence — a stale-but-still
      // -stored token from before a long sleep/idle period should not let
      // ProtectedBox render protected content while use-token-refresh's
      // own scheduled refresh is still catching up. Read-only on purpose
      // (no unauthenticate() call here): this runs during render via
      // ProtectedBox, and clearing state as a side effect of a getter
      // called mid-render would be unsafe. The refresh hook's failure path
      // is what actually clears the session; this just fails the check
      // fast so the redirect happens immediately either way.
      isAuthenticated: () => {
        const token = get().token.token;
        return !!token && !isJwtExpired(token);
      },
      authorize: (
        onSuccess: () => void,
        onFailure: (error: string) => void,
      ) => {
        const { token } = get();
        if (!token.token) {
          onFailure('Missing token');
          return;
        }
        fetchSession(token.token)
          .then(session => {
            const projectRoles = session.project_roles.map(toProjectRole);
            const currentProjectRole = get().currentProjectRole;
            const matchedRole = projectRoles.find(
              role => role.id === currentProjectRole.id,
            );

            set({
              currentUser: {
                ...get().currentUser,
                id: session.user.legacy_user_id ?? get().currentUser.id,
              },
              organizationRole: toOrganizationRole(session.organization_role),
              projectRoles,
              currentProjectRole:
                matchedRole || projectRoles[0] || currentProjectRole,
              featurePermissions:
                session.feature_permissions.map(toFeaturePermission),
            });
            onSuccess();
          })
          .catch(err => {
            onFailure(
              err instanceof Error ? err.message : 'Authorization failed',
            );
          });
      },
      projectRoles: [],
      organizationRole: {} as AuthOrganizationRole,
      currentProjectRole: {} as AuthProjectRole,
      setCurrentProjectRole: (p: AuthProjectRole) =>
        set({ currentProjectRole: p }),
      isThereOrganization: () => {
        const state = get();
        return (
          state.organizationRole &&
          Object.keys(state.organizationRole).length > 0
        );
      },
      isThereProject: () => {
        return get().projectRoles && Object.keys(get().projectRoles).length > 0;
      },
      featurePermissions: [],
      isFeatureEnable: (feature: string) => {
        for (const permission of get().featurePermissions) {
          const pattern = new RegExp(permission.feature);
          if (pattern.test(feature)) {
            return permission.isenable;
          }
        }
        return false;
      },

      unauthenticate: () => {
        localStorage.clear();
        set({
          currentUser: {} as AuthUser,
          token: {} as AuthToken,
          refreshToken: '',
          organizationRole: {} as AuthOrganizationRole,
          projectRoles: [],
          currentProjectRole: {} as AuthProjectRole,
          featurePermissions: [],
        });
      },
    }),
    {
      version: 2,
      onRehydrateStorage: () => state => {},
      name: 'rpd::__user',
    },
  ),
);
